import { createClient, Session } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!;

// Create Supabase client with proper configuration for tab switching
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
    flowType: "pkce",
  },
  global: {
    headers: {
      "x-client-info": "adapteach",
    },
  },
});

// Database types
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  asal_sekolah: string;
  role: string;
  avatar_url?: string;
  points?: number;
  created_at: string;
  updated_at: string;
}

export interface AuthUser {
  id: string;
  email: string;
  profile?: UserProfile;
}

// Session recovery helper for better reliability on Netlify
export const recoverSession = async (): Promise<{
  success: boolean;
  session?: Session | null;
  error?: string;
}> => {
  try {
    // First, try to get the current session
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      console.error("❌ Session recovery error:", error);
      return { success: false, error: error.message };
    }

    if (session) {
      return { success: true, session };
    }

    // If no session, check if there's a session in the URL (OAuth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get("access_token");
    const refreshToken = urlParams.get("refresh_token");

    if (accessToken && refreshToken) {
      const { data, error: setError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (setError) {
        console.error("❌ Error setting session from URL:", setError);
        return { success: false, error: setError.message };
      }

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      return { success: true, session: data.session };
    }

    return { success: true, session: null };
  } catch (error) {
    console.error("💥 Exception in session recovery:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

export interface LessonPlan {
  id: string;
  user_id: string;
  title: string;
  content: string;
  mata_pelajaran: string;
  jenjang_pendidikan: string;
  topik_tujuan: string;
  kriteria_tambahan?: string;
  created_at: string;
  updated_at: string;
}

export interface Assessment {
  id: string;
  user_id: string;
  title: string;
  content: string;
  mata_pelajaran: string;
  jenjang_pendidikan: string;
  topik_tujuan: string;
  tipe_pertanyaan: string;
  kriteria_tambahan?: string;
  created_at: string;
  updated_at: string;
}

export interface ChallengeType {
  id: string;
  name: string;
  description: string;
  points_reward: number;
  target_count: number;
  challenge_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserChallenge {
  id: string;
  user_id: string;
  challenge_type_id: string;
  current_progress: number;
  target_count: number;
  points_reward: number;
  is_completed: boolean;
  completed_at?: string;
  challenge_date: string;
  created_at: string;
  updated_at: string;
  challenge_types?: ChallengeType;
}

export interface UserActivity {
  id: string;
  user_id: string;
  activity_type: string;
  points_earned: number;
  description?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface ChallengeProgressResult {
  updated_progress: boolean;
  challenge_completed: boolean;
  points_awarded: number;
  completed_challenges: string[];
}

// Collaboration types
export interface Post {
  id: string;
  user_id: string;
  content: string;
  media_url?: string;
  media_type?: string;
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file_size?: number;
  created_at: string;
  updated_at: string;
  user_profiles?: UserProfile;
}

export interface Friend {
  id: string;
  user_id: string;
  friend_id: string;
  status: "pending" | "accepted" | "rejected";
  created_at: string;
  updated_at: string;
  user_profiles?: UserProfile;
  friend_profiles?: UserProfile;
}

// Notification types
export interface Notification {
  id: string;
  user_id: string;
  type:
    | "friend_request"
    | "collaboration_invite"
    | "challenge_reminder"
    | "system_announcement"
    | "challenge_completed";
  title: string;
  content: string;
  is_read: boolean;
  action_status:
    | "pending"
    | "accepted"
    | "rejected"
    | "completed"
    | "dismissed";
  related_entity_id?: string;
  related_entity_type?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Helper function to check if URL is a custom uploaded avatar (stored in Supabase Storage)
const isCustomUploadedAvatar = (avatarUrl?: string): boolean => {
  if (!avatarUrl) return false;

  // Static avatars start with /avatars/ and are local files
  if (avatarUrl.startsWith("/avatars/")) return false;

  // Custom avatars are Supabase Storage URLs containing the bucket name
  return avatarUrl.includes("/storage/v1/object/public/avatars/");
};

// Avatar management functions
export const uploadAvatar = async (
  userId: string,
  file: File
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: "Format file tidak didukung. Gunakan JPG, PNG, atau GIF.",
      };
    }

    // Validate file size (2MB limit)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      return {
        success: false,
        error: "Ukuran file terlalu besar. Maksimal 2MB.",
      };
    }

    // Create file extension from file type
    const fileExt = file.type.split("/")[1];
    const fileName = `avatar.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    // Upload file to Supabase Storage
    const { error } = await supabase.storage
      .from("avatars")
      .upload(filePath, file, {
        upsert: true, // Replace existing file
        contentType: file.type,
      });

    if (error) {
      console.error("Error uploading avatar:", error);
      return {
        success: false,
        error: "Gagal mengunggah avatar. Silakan coba lagi.",
      };
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("avatars").getPublicUrl(filePath);

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error("Exception in uploadAvatar:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengunggah avatar.",
    };
  }
};

export const removeAvatar = async (
  userId: string,
  avatarUrl?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // If no avatar URL provided or it's a static avatar, nothing to remove from storage
    if (!avatarUrl || !isCustomUploadedAvatar(avatarUrl)) {
      return { success: true }; // Nothing to remove from storage
    }

    // Extract file path from Supabase Storage URL
    const urlParts = avatarUrl.split("/");
    const bucketIndex = urlParts.findIndex(part => part === "avatars");

    if (bucketIndex === -1 || bucketIndex >= urlParts.length - 1) {
      console.error("Invalid avatar URL format:", avatarUrl);
      return { success: false, error: "Format URL avatar tidak valid." };
    }

    const filePath = urlParts.slice(bucketIndex + 1).join("/");

    // Remove file from Supabase Storage
    const { error } = await supabase.storage.from("avatars").remove([filePath]);

    if (error) {
      console.error("Error removing avatar:", error);
      return {
        success: false,
        error: "Gagal menghapus avatar. Silakan coba lagi.",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in removeAvatar:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat menghapus avatar.",
    };
  }
};

// Helper function to resize image (client-side)
export const resizeImage = (
  file: File,
  maxWidth: number = 200,
  maxHeight: number = 200,
  quality: number = 0.8
): Promise<File> => {
  return new Promise(resolve => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      // Set canvas dimensions
      canvas.width = maxWidth;
      canvas.height = maxHeight;

      // Fill with white background
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, maxWidth, maxHeight);

        // Center the image
        const x = (maxWidth - width) / 2;
        const y = (maxHeight - height) / 2;

        // Draw resized image
        ctx.drawImage(img, x, y, width, height);

        // Convert to blob
        canvas.toBlob(
          blob => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            } else {
              resolve(file); // Fallback to original file
            }
          },
          file.type,
          quality
        );
      } else {
        resolve(file); // Fallback to original file
      }
    };

    img.src = URL.createObjectURL(file);
  });
};

// Collaboration functions
export const uploadPostMedia = async (
  userId: string,
  file: File
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "video/mp4"];
    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: "Format file tidak didukung. Gunakan JPG, PNG, atau MP4.",
      };
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      return {
        success: false,
        error: "Ukuran file terlalu besar. Maksimal 50MB.",
      };
    }

    // Create file extension from file type
    const fileExt = file.type.split("/")[1];
    const fileName = `media_${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    // Upload file to Supabase Storage
    const { error } = await supabase.storage
      .from("post_media")
      .upload(filePath, file, {
        contentType: file.type,
      });

    if (error) {
      console.error("Error uploading media:", error);
      return {
        success: false,
        error: "Gagal mengunggah media. Silakan coba lagi.",
      };
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("post_media").getPublicUrl(filePath);

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error("Exception in uploadPostMedia:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengunggah media.",
    };
  }
};

export const uploadPostFile = async (
  userId: string,
  file: File
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ];
    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: "Format file tidak didukung. Gunakan PDF, DOC, atau PPT.",
      };
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return {
        success: false,
        error: "Ukuran file terlalu besar. Maksimal 10MB.",
      };
    }

    // Create file name with timestamp
    const fileName = `file_${Date.now()}_${file.name}`;
    const filePath = `${userId}/${fileName}`;

    // Upload file to Supabase Storage
    const { error } = await supabase.storage
      .from("post_files")
      .upload(filePath, file, {
        contentType: file.type,
      });

    if (error) {
      console.error("Error uploading file:", error);
      return {
        success: false,
        error: "Gagal mengunggah file. Silakan coba lagi.",
      };
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("post_files").getPublicUrl(filePath);

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error("Exception in uploadPostFile:", error);
    return { success: false, error: "Terjadi kesalahan saat mengunggah file." };
  }
};

export const createPost = async (
  userId: string,
  postData: {
    content: string;
    media_url?: string;
    media_type?: string;
    file_url?: string;
    file_name?: string;
    file_type?: string;
    file_size?: number;
  }
): Promise<{ success: boolean; data?: Post; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from("posts")
      .insert({
        user_id: userId,
        content: postData.content,
        media_url: postData.media_url,
        media_type: postData.media_type,
        file_url: postData.file_url,
        file_name: postData.file_name,
        file_type: postData.file_type,
        file_size: postData.file_size,
      })
      .select(
        `
        *,
        user_profiles (*)
      `
      )
      .single();

    if (error) {
      console.error("Error creating post:", error);
      return { success: false, error: "Gagal membuat post" };
    }

    return { success: true, data: data as Post };
  } catch (error) {
    console.error("Exception in createPost:", error);
    return { success: false, error: "Terjadi kesalahan saat membuat post" };
  }
};

export const getPosts = async (
  limit: number = 10,
  offset: number = 0
): Promise<{ success: boolean; data?: Post[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from("posts")
      .select(
        `
        *,
        user_profiles (*)
      `
      )
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error("Error fetching posts:", error);
      return { success: false, error: "Gagal mengambil data posts" };
    }

    return { success: true, data: data as Post[] };
  } catch (error) {
    console.error("Exception in getPosts:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil data posts",
    };
  }
};

export const subscribeToPosts = (callback: (post: Post) => void) => {
  const subscription = supabase
    .channel("posts")
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "posts",
      },
      async payload => {
        // Fetch the complete post data with user profile
        const { data } = await supabase
          .from("posts")
          .select(
            `
            *,
            user_profiles (*)
          `
          )
          .eq("id", payload.new.id)
          .single();

        if (data) {
          callback(data as Post);
        }
      }
    )
    .subscribe();

  return subscription;
};

export const getFriendRecommendations = async (
  userId: string,
  limit: number = 5,
  offset: number = 0
): Promise<{ success: boolean; data?: UserProfile[]; error?: string }> => {
  try {
    // First, get only accepted friend relationships for the current user
    const { data: friendsData, error: friendsError } = await supabase
      .from("friends")
      .select("user_id, friend_id")
      .or(`user_id.eq.${userId},friend_id.eq.${userId}`)
      .eq("status", "accepted"); // Only exclude accepted friends

    if (friendsError) {
      console.error("Error fetching friends:", friendsError);
      return { success: false, error: "Gagal mengambil data teman" };
    }

    // Extract all user IDs that should be excluded (current user + accepted friends only)
    const excludedIds = new Set([userId]);

    if (friendsData) {
      friendsData.forEach(friend => {
        if (friend.user_id === userId) {
          excludedIds.add(friend.friend_id);
        } else {
          excludedIds.add(friend.user_id);
        }
      });
    }

    const excludedIdsArray = Array.from(excludedIds);

    // Get users that are not in the excluded list with pagination
    const { data, error } = await supabase
      .from("user_profiles")
      .select("*")
      .filter("id", "not.in", `(${excludedIdsArray.join(",")})`)
      .range(offset, offset + limit - 1);

    if (error) {
      console.error("Error fetching friend recommendations:", error);
      return { success: false, error: "Gagal mengambil rekomendasi teman" };
    }

    return { success: true, data: data as UserProfile[] };
  } catch (error) {
    console.error("Exception in getFriendRecommendations:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil rekomendasi teman",
    };
  }
};

export const getSentFriendRequests = async (
  userId: string
): Promise<{ success: boolean; data?: Friend[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from("friends")
      .select(
        `
        *,
        friend_profiles:user_profiles!friends_friend_id_fkey (*)
      `
      )
      .eq("user_id", userId)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching sent friend requests:", error);
      return {
        success: false,
        error: "Gagal mengambil permintaan pertemanan yang dikirim",
      };
    }

    return { success: true, data: data as Friend[] };
  } catch (error) {
    console.error("Exception in getSentFriendRequests:", error);
    return {
      success: false,
      error:
        "Terjadi kesalahan saat mengambil permintaan pertemanan yang dikirim",
    };
  }
};

export const addFriend = async (
  userId: string,
  friendId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.from("friends").insert({
      user_id: userId,
      friend_id: friendId,
      status: "pending",
    });

    if (error) {
      console.error("Error adding friend:", error);
      return { success: false, error: "Gagal menambahkan teman" };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in addFriend:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat menambahkan teman",
    };
  }
};

export const getPendingFriendRequests = async (
  userId: string
): Promise<{ success: boolean; data?: Friend[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from("friends")
      .select(
        `
        *,
        user_profiles!friends_user_id_fkey (*)
      `
      )
      .eq("friend_id", userId)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching pending friend requests:", error);
      return { success: false, error: "Gagal mengambil permintaan pertemanan" };
    }

    return { success: true, data: data as Friend[] };
  } catch (error) {
    console.error("Exception in getPendingFriendRequests:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil permintaan pertemanan",
    };
  }
};

export const acceptFriendRequest = async (
  requestId: string,
  currentUserId: string,
  senderId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Start a transaction-like operation
    // First, update the original request to accepted
    const { error: updateError } = await supabase
      .from("friends")
      .update({
        status: "accepted",
        updated_at: new Date().toISOString(),
      })
      .eq("id", requestId);

    if (updateError) {
      console.error("Error updating friend request:", updateError);
      return { success: false, error: "Gagal menerima permintaan pertemanan" };
    }

    // Then, create the reciprocal relationship
    const { error: insertError } = await supabase.from("friends").insert({
      user_id: currentUserId,
      friend_id: senderId,
      status: "accepted",
    });

    if (insertError) {
      console.error("Error creating reciprocal friendship:", insertError);
      return { success: false, error: "Gagal membuat hubungan pertemanan" };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in acceptFriendRequest:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat menerima permintaan pertemanan",
    };
  }
};

export const rejectFriendRequest = async (
  requestId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from("friends")
      .update({
        status: "rejected",
        updated_at: new Date().toISOString(),
      })
      .eq("id", requestId);

    if (error) {
      console.error("Error rejecting friend request:", error);
      return { success: false, error: "Gagal menolak permintaan pertemanan" };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in rejectFriendRequest:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat menolak permintaan pertemanan",
    };
  }
};

export const getCurrentFriends = async (
  userId: string
): Promise<{ success: boolean; data?: Friend[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from("friends")
      .select(
        `
        *,
        friend_profiles:user_profiles!friends_friend_id_fkey (*)
      `
      )
      .eq("user_id", userId)
      .eq("status", "accepted")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching current friends:", error);
      return { success: false, error: "Gagal mengambil daftar teman" };
    }

    return { success: true, data: data as Friend[] };
  } catch (error) {
    console.error("Exception in getCurrentFriends:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil daftar teman",
    };
  }
};

export const removeFriend = async (
  userId: string,
  friendId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Remove both directions of the friendship
    const { error: error1 } = await supabase
      .from("friends")
      .delete()
      .eq("user_id", userId)
      .eq("friend_id", friendId);

    const { error: error2 } = await supabase
      .from("friends")
      .delete()
      .eq("user_id", friendId)
      .eq("friend_id", userId);

    if (error1 || error2) {
      console.error("Error removing friend:", error1 || error2);
      return { success: false, error: "Gagal menghapus teman" };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in removeFriend:", error);
    return { success: false, error: "Terjadi kesalahan saat menghapus teman" };
  }
};

// Notification functions
export const createNotification = async (
  userId: string,
  type: string,
  title: string,
  content: string,
  relatedEntityId?: string,
  relatedEntityType?: string,
  metadata?: Record<string, unknown>
): Promise<{ success: boolean; data?: Notification; error?: string }> => {
  try {
    const { error } = await supabase.rpc("create_notification", {
      recipient_user_id: userId,
      notification_type: type,
      notification_title: title,
      notification_content: content,
      entity_id: relatedEntityId || null,
      entity_type: relatedEntityType || null,
      notification_metadata: metadata || {},
    });

    if (error) {
      console.error("Error creating notification:", error);
      return { success: false, error: "Gagal membuat notifikasi" };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in createNotification:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat membuat notifikasi",
    };
  }
};

export const getNotifications = async (
  userId: string,
  limit: number = 20,
  offset: number = 0
): Promise<{ success: boolean; data?: Notification[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error("Error fetching notifications:", error);
      return { success: false, error: "Gagal mengambil notifikasi" };
    }

    return { success: true, data: data as Notification[] };
  } catch (error) {
    console.error("Exception in getNotifications:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil notifikasi",
    };
  }
};

export const getUnreadNotificationCount = async (
  userId: string
): Promise<{ success: boolean; count?: number; error?: string }> => {
  try {
    const { count, error } = await supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("is_read", false);

    if (error) {
      console.error("Error fetching unread notification count:", error);
      return {
        success: false,
        error: "Gagal mengambil jumlah notifikasi belum dibaca",
      };
    }

    return { success: true, count: count || 0 };
  } catch (error) {
    console.error("Exception in getUnreadNotificationCount:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil jumlah notifikasi belum dibaca",
    };
  }
};

export const markNotificationAsRead = async (
  notificationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.rpc("mark_notification_as_read", {
      notification_id: notificationId,
      user_id_param: userId,
    });

    if (error) {
      console.error("Error marking notification as read:", error);
      return {
        success: false,
        error: "Gagal menandai notifikasi sebagai dibaca",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in markNotificationAsRead:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat menandai notifikasi sebagai dibaca",
    };
  }
};

export const markAllNotificationsAsRead = async (
  userId: string
): Promise<{ success: boolean; count?: number; error?: string }> => {
  try {
    const { data, error } = await supabase.rpc(
      "mark_all_notifications_as_read",
      {
        user_id_param: userId,
      }
    );

    if (error) {
      console.error("Error marking all notifications as read:", error);
      return {
        success: false,
        error: "Gagal menandai semua notifikasi sebagai dibaca",
      };
    }

    return { success: true, count: data };
  } catch (error) {
    console.error("Exception in markAllNotificationsAsRead:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat menandai semua notifikasi sebagai dibaca",
    };
  }
};

export const updateNotificationActionStatus = async (
  notificationId: string,
  userId: string,
  actionStatus: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from("notifications")
      .update({
        action_status: actionStatus,
        is_read: true,
        updated_at: new Date().toISOString(),
      })
      .eq("id", notificationId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error updating notification action status:", error);
      return {
        success: false,
        error: "Gagal memperbarui status aksi notifikasi",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in updateNotificationActionStatus:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat memperbarui status aksi notifikasi",
    };
  }
};

export const subscribeToNotifications = (
  userId: string,
  callback: (notification: Notification) => void
) => {
  const subscription = supabase
    .channel(`notifications:${userId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${userId}`,
      },
      payload => {
        callback(payload.new as Notification);
      }
    )
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${userId}`,
      },
      payload => {
        callback(payload.new as Notification);
      }
    )
    .subscribe();

  return subscription;
};

// Gamification functions
export const getUserChallenges = async (
  userId: string
): Promise<{ success: boolean; data?: UserChallenge[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from("user_challenges")
      .select(
        `
        *,
        challenge_types (*)
      `
      )
      .eq("user_id", userId)
      .eq("challenge_date", new Date().toISOString().split("T")[0])
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching user challenges:", error);
      return { success: false, error: "Gagal mengambil data tantangan" };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Exception in getUserChallenges:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil data tantangan",
    };
  }
};

export const initializeDailyChallenges = async (
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.rpc("initialize_daily_challenges", {
      user_id_param: userId,
    });

    if (error) {
      console.error("Error initializing daily challenges:", error);
      return {
        success: false,
        error: "Gagal menginisialisasi tantangan harian",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in initializeDailyChallenges:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat menginisialisasi tantangan harian",
    };
  }
};

export const updateChallengeProgress = async (
  userId: string,
  activityType: string,
  points: number = 0
): Promise<{
  success: boolean;
  data?: ChallengeProgressResult;
  error?: string;
}> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { data, error } = await supabase.rpc("update_challenge_progress", {
      user_id_param: userId,
      activity_type_param: activityType,
      points_param: points,
    });

    if (error) {
      console.error("Error updating challenge progress:", error);
      return { success: false, error: "Gagal memperbarui progress tantangan" };
    }

    return { success: true, data: data as ChallengeProgressResult };
  } catch (error) {
    console.error("Exception in updateChallengeProgress:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat memperbarui progress tantangan",
    };
  }
};

export const getUserActivities = async (
  userId: string,
  limit: number = 10
): Promise<{ success: boolean; data?: UserActivity[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from("user_activities")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error fetching user activities:", error);
      return { success: false, error: "Gagal mengambil data aktivitas" };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Exception in getUserActivities:", error);
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil data aktivitas",
    };
  }
};

// History functions for lesson plans
interface LessonPlanInput {
  title: string;
  content: string;
  mata_pelajaran: string;
  jenjang_pendidikan: string;
  topik_tujuan: string;
  kriteria_tambahan?: string;
}

export const saveLessonPlan = async (
  userId: string,
  lessonPlanData: LessonPlanInput
): Promise<{ success: boolean; data?: { id: string }; error?: string }> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { data, error } = await supabase
      .from("lesson_plans")
      .insert({
        user_id: userId,
        title: lessonPlanData.title,
        content: lessonPlanData.content,
        mata_pelajaran: lessonPlanData.mata_pelajaran,
        jenjang_pendidikan: lessonPlanData.jenjang_pendidikan,
        topik_tujuan: lessonPlanData.topik_tujuan,
        kriteria_tambahan: lessonPlanData.kriteria_tambahan,
      })
      .select("id") // ✅ PERBAIKAN KRITIS: Hanya meminta ID
      .single();

    if (error) {
      console.error("Error saving lesson plan:", error);
      Sentry.captureException(error, {
        tags: {
          function: "saveLessonPlan",
          operation: "insert",
        },
        extra: {
          user_id: userId,
          lesson_plan: {
            mata_pelajaran: lessonPlanData.mata_pelajaran,
            jenjang_pendidikan: lessonPlanData.jenjang_pendidikan,
          },
        },
      });
      return { success: false, error: "Gagal menyimpan rencana pembelajaran" };
    }

    // Mengembalikan data sebagai objek { id: string }
    return { success: true, data: data as { id: string } };
  } catch (error) {
    console.error("Exception in saveLessonPlan:", error);
    Sentry.captureException(error, {
      tags: {
        function: "saveLessonPlan",
        operation: "exception",
      },
      extra: {
        user_id: userId,
      },
    });
    return {
      success: false,
      error: "Terjadi kesalahan saat menyimpan rencana pembelajaran",
    };
  }
};

export const updateLessonPlan = async (
  userId: string,
  lessonPlanId: string,
  lessonPlanData: {
    title: string;
    content: string;
    mata_pelajaran: string;
    jenjang_pendidikan: string;
    topik_tujuan: string;
    kriteria_tambahan?: string;
  }
): Promise<{ success: boolean; data?: LessonPlan; error?: string }> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { data, error } = await supabase
      .from("lesson_plans")
      .update({
        title: lessonPlanData.title,
        content: lessonPlanData.content,
        mata_pelajaran: lessonPlanData.mata_pelajaran,
        jenjang_pendidikan: lessonPlanData.jenjang_pendidikan,
        topik_tujuan: lessonPlanData.topik_tujuan,
        kriteria_tambahan: lessonPlanData.kriteria_tambahan,
        updated_at: new Date().toISOString(),
      })
      .eq("id", lessonPlanId)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating lesson plan:", error);
      Sentry.captureException(error, {
        tags: {
          function: "updateLessonPlan",
          operation: "update",
        },
        extra: {
          user_id: userId,
          lesson_plan_id: lessonPlanId,
        },
      });
      return {
        success: false,
        error: "Gagal memperbarui rencana pembelajaran",
      };
    }

    return { success: true, data: data as LessonPlan };
  } catch (error) {
    console.error("Exception in updateLessonPlan:", error);
    Sentry.captureException(error, {
      tags: {
        function: "updateLessonPlan",
        operation: "exception",
      },
      extra: {
        user_id: userId,
        lesson_plan_id: lessonPlanId,
      },
    });
    return {
      success: false,
      error: "Terjadi kesalahan saat memperbarui rencana pembelajaran",
    };
  }
};

export const getLessonPlans = async (
  userId: string,
  limit: number = 50
): Promise<{ success: boolean; data?: LessonPlan[]; error?: string }> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { data, error } = await supabase
      .from("lesson_plans")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      Sentry.captureException(error, {
        tags: {
          function: "getLessonPlans",
          operation: "select",
        },
        extra: {
          user_id: userId,
          limit,
        },
      });
      return {
        success: false,
        error: "Gagal mengambil data rencana pembelajaran",
      };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        function: "getLessonPlans",
        operation: "exception",
      },
      extra: {
        user_id: userId,
      },
    });
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil data rencana pembelajaran",
    };
  }
};

export const deleteLessonPlan = async (
  userId: string,
  lessonPlanId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { error } = await supabase
      .from("lesson_plans")
      .delete()
      .eq("id", lessonPlanId)
      .eq("user_id", userId);

    if (error) {
      Sentry.captureException(error, {
        tags: {
          function: "deleteLessonPlan",
          operation: "delete",
        },
        extra: {
          user_id: userId,
          lesson_plan_id: lessonPlanId,
        },
      });
      return { success: false, error: "Gagal menghapus rencana pembelajaran" };
    }

    return { success: true };
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        function: "deleteLessonPlan",
        operation: "exception",
      },
      extra: {
        user_id: userId,
        lesson_plan_id: lessonPlanId,
      },
    });
    return {
      success: false,
      error: "Terjadi kesalahan saat menghapus rencana pembelajaran",
    };
  }
};

// History functions for assessments
interface AssessmentInput {
  title: string;
  content: string;
  mata_pelajaran: string;
  jenjang_pendidikan: string;
  topik_tujuan: string;
  tipe_pertanyaan: string;
  kriteria_tambahan?: string;
}

export const saveAssessment = async (
  userId: string,
  assessmentData: AssessmentInput
): Promise<{ success: boolean; data?: { id: string }; error?: string }> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { data, error } = await supabase
      .from("assessments")
      .insert({
        user_id: userId,
        title: assessmentData.title,
        content: assessmentData.content,
        mata_pelajaran: assessmentData.mata_pelajaran,
        jenjang_pendidikan: assessmentData.jenjang_pendidikan,
        topik_tujuan: assessmentData.topik_tujuan,
        tipe_pertanyaan: assessmentData.tipe_pertanyaan,
        kriteria_tambahan: assessmentData.kriteria_tambahan,
      })
      .select("id") // ✅ PERBAIKAN KRITIS: Hanya meminta ID
      .single();

    if (error) {
      console.error("Error saving assessment:", error);
      Sentry.captureException(error, {
        tags: {
          function: "saveAssessment",
          operation: "insert",
        },
        extra: {
          user_id: userId,
          assessment: {
            mata_pelajaran: assessmentData.mata_pelajaran,
            jenjang_pendidikan: assessmentData.jenjang_pendidikan,
            tipe_pertanyaan: assessmentData.tipe_pertanyaan,
          },
        },
      });
      return { success: false, error: "Gagal menyimpan soal assessment" };
    }

    // Mengembalikan data sebagai objek { id: string }
    return { success: true, data: data as { id: string } };
  } catch (error) {
    console.error("Exception in saveAssessment:", error);
    Sentry.captureException(error, {
      tags: {
        function: "saveAssessment",
        operation: "exception",
      },
      extra: {
        user_id: userId,
      },
    });
    return {
      success: false,
      error: "Terjadi kesalahan saat menyimpan soal assessment",
    };
  }
};

export const updateAssessment = async (
  userId: string,
  assessmentId: string,
  assessmentData: {
    title: string;
    content: string;
    mata_pelajaran: string;
    jenjang_pendidikan: string;
    topik_tujuan: string;
    tipe_pertanyaan: string;
    kriteria_tambahan?: string;
  }
): Promise<{ success: boolean; data?: Assessment; error?: string }> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { data, error } = await supabase
      .from("assessments")
      .update({
        title: assessmentData.title,
        content: assessmentData.content,
        mata_pelajaran: assessmentData.mata_pelajaran,
        jenjang_pendidikan: assessmentData.jenjang_pendidikan,
        topik_tujuan: assessmentData.topik_tujuan,
        tipe_pertanyaan: assessmentData.tipe_pertanyaan,
        kriteria_tambahan: assessmentData.kriteria_tambahan,
        updated_at: new Date().toISOString(),
      })
      .eq("id", assessmentId)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating assessment:", error);
      Sentry.captureException(error, {
        tags: {
          function: "updateAssessment",
          operation: "update",
        },
        extra: {
          user_id: userId,
          assessment_id: assessmentId,
        },
      });
      return { success: false, error: "Gagal memperbarui soal assessment" };
    }

    return { success: true, data: data as Assessment };
  } catch (error) {
    console.error("Exception in updateAssessment:", error);
    Sentry.captureException(error, {
      tags: {
        function: "updateAssessment",
        operation: "exception",
      },
      extra: {
        user_id: userId,
        assessment_id: assessmentId,
      },
    });
    return {
      success: false,
      error: "Terjadi kesalahan saat memperbarui soal assessment",
    };
  }
};

export const getAssessments = async (
  userId: string,
  limit: number = 50
): Promise<{ success: boolean; data?: Assessment[]; error?: string }> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { data, error } = await supabase
      .from("assessments")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      Sentry.captureException(error, {
        tags: {
          function: "getAssessments",
          operation: "select",
        },
        extra: {
          user_id: userId,
          limit,
        },
      });
      return { success: false, error: "Gagal mengambil data soal assessment" };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        function: "getAssessments",
        operation: "exception",
      },
      extra: {
        user_id: userId,
      },
    });
    return {
      success: false,
      error: "Terjadi kesalahan saat mengambil data soal assessment",
    };
  }
};

export const deleteAssessment = async (
  userId: string,
  assessmentId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: "Sesi tidak valid. Silakan login kembali.",
      };
    }

    const { error } = await supabase
      .from("assessments")
      .delete()
      .eq("id", assessmentId)
      .eq("user_id", userId);

    if (error) {
      Sentry.captureException(error, {
        tags: {
          function: "deleteAssessment",
          operation: "delete",
        },
        extra: {
          user_id: userId,
          assessment_id: assessmentId,
        },
      });
      return { success: false, error: "Gagal menghapus soal assessment" };
    }

    return { success: true };
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        function: "deleteAssessment",
        operation: "exception",
      },
      extra: {
        user_id: userId,
        assessment_id: assessmentId,
      },
    });
    return {
      success: false,
      error: "Terjadi kesalahan saat menghapus soal assessment",
    };
  }
};

export const getLessonPlanById = async (id: string) => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return { data: null, error: "Sesi tidak valid. Silakan login kembali." };
    }

    const { data, error } = await supabase
      .from("lesson_plans")
      .select("*")
      .eq("id", id)
      .maybeSingle(); // Tetap gunakan .single() setelah .limit(1)

    if (error) {
      console.error("Supabase Error:", error);
      Sentry.captureException(error, {
        tags: {
          function: "getLessonPlanById",
          operation: "select",
        },
        extra: {
          lesson_plan_id: id,
        },
      });
      return { data: null, error: error.message };
    }

    if (!data) {
      return { data: null, error: "Lesson plan not found." };
    }

    return { data, error: null };
  } catch (err) {
    console.error("Unexpected Error:", err);
    Sentry.captureException(err, {
      tags: {
        function: "getLessonPlanById",
        operation: "exception",
      },
      extra: {
        lesson_plan_id: id,
      },
    });
    return { data: null, error: "An unexpected error occurred." };
  }
};

// src/lib/supabase.ts atau file lain tempat fungsi getLessonPlanById berada

export const getAssessmentById = async (id: string) => {
  try {
    // Use getUser() which automatically refreshes tokens if expired
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return { data: null, error: "Sesi tidak valid. Silakan login kembali." };
    }

    const { data, error } = await supabase
      .from("assessments") // 👈 ubah ke nama tabel assessment kamu
      .select("*")
      .eq("id", id)
      .maybeSingle(); // supaya tidak error kalau data kosong

    if (error) {
      console.error("Supabase Error (Assessment):", error);
      Sentry.captureException(error, {
        tags: {
          function: "getAssessmentById",
          operation: "select",
        },
        extra: {
          assessment_id: id,
        },
      });
      return { data: null, error: error.message };
    }

    if (!data) {
      return { data: null, error: "Assessment not found." };
    }

    return { data, error: null };
  } catch (err) {
    console.error("Unexpected Error (Assessment):", err);
    Sentry.captureException(err, {
      tags: {
        function: "getAssessmentById",
        operation: "exception",
      },
      extra: {
        assessment_id: id,
      },
    });
    return {
      data: null,
      error: "An unexpected error occurred while fetching assessment.",
    };
  }
};
